import React, { useState, useMemo, useCallback, useEffect } from "react";
import { PostFormData, CreateCommanWay, ListCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button } from "@heroui/react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline";

// ──────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────
interface VerifyResult {
  refCode: string;
  type: string;
  recordId: number;
  status: string | null;
  flag: "VALID" | "DUPLICATE" | "ALREADY_PAID" | "CANCELLED" | "NOT_FOUND";
  vendorRemark: string | null;
  vendorAmount: number | null;
  vendorDate: string | null;
  ourDate: string | null;
  dateDiffDays: number | null;
  dateWarning: boolean;
}

interface VerifySummary {
  total: number;
  valid: number;
  duplicate: number;
  alreadyPaid: number;
  cancelled: number;
  notFound: number;
  totalVendorAmount: number;
  totalValidAmount: number;
}

// ── Auto-detect helpers ──
const REF_CODE_RE = /^[A-Za-z]{1,3}\d{4,13}$/;
const DATE_RE = /^\d{1,2}[-/.]\d{1,2}[-/.]\d{2,4}$/;
const EXCEL_SERIAL_RE = /^\d{5}$/;
const isRefCode = (val: string) => REF_CODE_RE.test(val.trim());
const isDateLike = (val: string) => {
  const s = val.trim();
  return DATE_RE.test(s) || EXCEL_SERIAL_RE.test(s);
};

const flagColors: Record<string, string> = {
  VALID: "bg-green-100 text-green-800",
  DUPLICATE: "bg-orange-100 text-orange-800",
  ALREADY_PAID: "bg-red-100 text-red-800",
  CANCELLED: "bg-red-100 text-red-700",
  NOT_FOUND: "bg-gray-100 text-gray-600",
};

const STEPS = ["Upload & Extract", "Review & Verify", "Confirmation"];

// ──────────────────────────────────────────────
// Step Indicator
// ──────────────────────────────────────────────
const StepIndicator = ({ current }: { current: number }) => (
  <div className="flex items-center justify-center mb-6">
    {STEPS.map((label, i) => {
      const stepNum = i + 1;
      const isActive = stepNum === current;
      const isDone = stepNum < current;
      return (
        <React.Fragment key={label}>
          {i > 0 && <div className={`h-0.5 w-16 mx-1 ${isDone ? "bg-green-500" : "bg-gray-300"}`} />}
          <div className="flex flex-col items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
                isDone
                  ? "bg-green-500 border-green-500 text-white"
                  : isActive
                  ? "border-blue-600 text-blue-600 bg-blue-50"
                  : "border-gray-300 text-gray-400"
              }`}
            >
              {isDone ? "✓" : stepNum}
            </div>
            <span className={`text-xs mt-1 ${isActive ? "text-blue-600 font-medium" : "text-gray-400"}`}>{label}</span>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);

// ──────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────
const VendorInvoiceCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1 state
  const [file, setFile] = useState<File | null>(null);
  const [extractedRows, setExtractedRows] = useState<string[][]>([]);
  const [fileType, setFileType] = useState<string>("");
  const [startRow, setStartRow] = useState(1); // 0-based index, default 1 (skip header)
  const [endRow, setEndRow] = useState(-1); // -1 means last row (inclusive)
  const [refCodeCol, setRefCodeCol] = useState(0);
  const [amountCol, setAmountCol] = useState(-1); // -1 means not selected
  const [dateCol, setDateCol] = useState(-1); // -1 means not selected

  // Step 2 state
  const [verifyResults, setVerifyResults] = useState<VerifyResult[]>([]);
  const [verifySummary, setVerifySummary] = useState<VerifySummary | null>(null);
  const [checkedRefs, setCheckedRefs] = useState<Set<number>>(new Set());

  // Invoice form state
  const [vendorId, setVendorId] = useState<any>(null);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [verifiedAmount, setVerifiedAmount] = useState("");
  const [remark, setRemark] = useState("");

  // Per-row remark map (refCode -> user-entered remark)
  const [remarkMap, setRemarkMap] = useState<Record<string, string>>({});

  // Per-row refCode edits (index -> edited refCode)
  const [refCodeEdits, setRefCodeEdits] = useState<Record<number, string>>({});

  // Step 3 state
  const [createdInvoice, setCreatedInvoice] = useState<any>(null);

  // Vendor options
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  useQuery(["vendorListForCreate"], () => ListCommanWay("/admin/vendor/list", {}, { page: 1, paginate: 1000 }), {
    onSuccess: (res: any) => {
      if (res?.status === "SUCCESS") {
        const opts = (res?.data?.data || []).map((v: any) => ({
          value: v.id,
          label: v.vendor_name || v.companyName || `Vendor #${v.id}`,
        }));
        setVendorOptions(opts);
      }
    },
  });

  // ── Step 1: Extract file ──
  const { mutate: extractFile, isLoading: extracting } = useMutation(
    (formData: FormData) => PostFormData("/admin/vendor-invoice/extract", formData),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS" && res?.data?.rows?.length) {
          const rows: string[][] = res.data.rows;
          setExtractedRows(rows);
          setFileType(res.data.type || "");

          // ── Auto-detect ref code column ──
          const numCols = rows[0]?.length || 0;
          let bestRefCol = 0, bestRefCount = 0;
          for (let c = 0; c < numCols; c++) {
            let count = 0;
            for (let r = 1; r < rows.length; r++) {
              if (isRefCode((rows[r]?.[c] || "").toString())) count++;
            }
            if (count > bestRefCount) { bestRefCount = count; bestRefCol = c; }
          }
          setRefCodeCol(bestRefCol);

          // ── Auto-detect start / end rows from ref code column ──
          let first = -1, last = -1;
          for (let r = 0; r < rows.length; r++) {
            if (isRefCode((rows[r]?.[bestRefCol] || "").toString())) {
              if (first === -1) first = r;
              last = r;
            }
          }
          setStartRow(first >= 0 ? first : 1);
          setEndRow(last >= 0 ? last : rows.length - 1);

          // ── Auto-detect date column ──
          const rangeStart = first >= 0 ? first : 1;
          const rangeEnd = last >= 0 ? last : rows.length - 1;
          let bestDateCol = -1, bestDateCount = 0;
          for (let c = 0; c < numCols; c++) {
            if (c === bestRefCol) continue;
            let count = 0;
            for (let r = rangeStart; r <= rangeEnd; r++) {
              if (isDateLike((rows[r]?.[c] || "").toString())) count++;
            }
            if (count > bestDateCount) { bestDateCount = count; bestDateCol = c; }
          }
          const dataCount = rangeEnd - rangeStart + 1;
          setDateCol(bestDateCount >= dataCount * 0.3 && bestDateCol >= 0 ? bestDateCol : -1);
          setAmountCol(-1);

          UseToast(`Extracted ${res.data.totalRows} rows`, "success");
        } else {
          UseToast(res?.message || "Failed to extract data from file", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err?.message || "Extract failed", "error");
      },
    }
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setExtractedRows([]);
    }
  };

  const handleUpload = () => {
    if (!file) {
      UseToast("Please select a file first", "error");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    extractFile(formData);
  };

  // Effective end row (inclusive, 0-based)
  const effectiveEndRow = endRow >= startRow ? endRow : extractedRows.length - 1;

  // Slice data rows (startRow to effectiveEndRow inclusive)
  const dataRows = useMemo(() => {
    if (!extractedRows.length) return [];
    return extractedRows.slice(startRow, effectiveEndRow + 1);
  }, [extractedRows, startRow, effectiveEndRow]);

  // Extract ref codes from chosen column
  const extractedRefCodes = useMemo(() => {
    if (!dataRows.length || refCodeCol < 0) return [];
    return dataRows.map((row) => (row[refCodeCol] || "").toString().trim()).filter(Boolean);
  }, [dataRows, refCodeCol]);

  // Extract amounts from chosen column (parallel to refCodes)
  const extractedAmounts = useMemo(() => {
    if (!dataRows.length || amountCol < 0) return [];
    return dataRows.map((row) => {
      const raw = (row[amountCol] || "").toString().replace(/[^\d.\-]/g, "");
      const num = parseFloat(raw);
      return isNaN(num) ? null : num;
    });
  }, [dataRows, amountCol]);

  // Extract dates from chosen column (parallel to refCodes)
  const extractedDates = useMemo(() => {
    if (!dataRows.length || dateCol < 0) return [];
    return dataRows.map((row) => (row[dateCol] || "").toString().trim());
  }, [dataRows, dateCol]);

  // Column options (labeled from first row)
  const columnOptions = useMemo(() => {
    if (!extractedRows.length) return [];
    return extractedRows[0].map((cell, i) => ({
      value: i,
      label: `Col ${i + 1}: ${cell || "(empty)"}`,
    }));
  }, [extractedRows]);

  // Row options
  const rowOptions = useMemo(() => {
    return extractedRows.map((_, i) => ({
      value: i,
      label: `Row ${i + 1}`,
    }));
  }, [extractedRows]);

  // ── Step 2: Verify ref codes ──
  const { mutate: verifyRefs, isLoading: verifying } = useMutation(
    (payload: { refCodes: string[]; amounts?: (number | null)[]; dates?: string[] }) =>
      CreateCommanWay({ url: "/admin/vendor-invoice/verify", data: payload }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS" && res?.data) {
          // Post-process: first occurrence of a DUPLICATE refCode → VALID
          const seen = new Set<string>();
          const processed: VerifyResult[] = (res.data.results || []).map((r: VerifyResult) => {
            if (r.flag === "DUPLICATE" && !seen.has(r.refCode)) {
              seen.add(r.refCode);
              return { ...r, flag: "VALID" as const };
            }
            seen.add(r.refCode);
            return r;
          });
          setVerifyResults(processed);
          setVerifySummary(res.data.summary || null);

          // Pre-check VALID items (by row index)
          const validIndices = new Set<number>();
          processed.forEach((r: VerifyResult, idx: number) => {
            if (r.flag === "VALID") validIndices.add(idx);
          });
          setCheckedRefs(validIndices);

          // Auto-fill totalAmount from sum of all vendor amounts
          const allAmtSum = processed.reduce(
            (sum: number, r: VerifyResult) => sum + (r.vendorAmount || 0),
            0
          );
          if (allAmtSum > 0) setTotalAmount(allAmtSum.toString());

          // Auto-fill verifiedAmount from sum of valid items
          const validAmtSum = processed.reduce(
            (sum: number, r: VerifyResult) => sum + (r.flag === "VALID" ? (r.vendorAmount || 0) : 0),
            0
          );
          if (validAmtSum > 0) setVerifiedAmount(validAmtSum.toString());
        } else {
          UseToast(res?.message || "Verification failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err?.message || "Verification failed", "error");
      },
    }
  );

  // Recalculate verifiedAmount whenever checked items change
  const checkedAmountSum = useMemo(() => {
    let sum = 0;
    verifyResults.forEach((r, i) => {
      if (checkedRefs.has(i)) sum += r.vendorAmount || 0;
    });
    return sum;
  }, [checkedRefs, verifyResults]);

  // Sync verifiedAmount with checkedAmountSum
  React.useEffect(() => {
    if (checkedAmountSum > 0) setVerifiedAmount(checkedAmountSum.toString());
  }, [checkedAmountSum]);

  const handleGoToStep2 = () => {
    if (extractedRefCodes.length === 0) {
      UseToast("No ref codes extracted. Please check your row/column selection.", "error");
      return;
    }
    setStep(2);
    const payload: { refCodes: string[]; amounts?: (number | null)[]; dates?: string[] } = {
      refCodes: extractedRefCodes,
    };
    if (amountCol >= 0 && extractedAmounts.length) payload.amounts = extractedAmounts;
    if (dateCol >= 0 && extractedDates.length) payload.dates = extractedDates;
    verifyRefs(payload);
  };

  // Check if any refCode was edited (to show Re-verify button)
  const hasRefCodeEdits = useMemo(() => {
    return Object.keys(refCodeEdits).some(
      (idx) => refCodeEdits[Number(idx)] !== verifyResults[Number(idx)]?.refCode
    );
  }, [refCodeEdits, verifyResults]);

  // Re-verify with edited refCodes
  const handleReverify = () => {
    const updatedRefCodes = verifyResults.map((r, i) =>
      refCodeEdits[i] !== undefined ? refCodeEdits[i].trim() : r.refCode
    );
    const updatedAmounts = verifyResults.map((r) => r.vendorAmount);
    const updatedDates = verifyResults.map((r) => r.vendorDate || "");
    const payload: { refCodes: string[]; amounts?: (number | null)[]; dates?: string[] } = {
      refCodes: updatedRefCodes,
    };
    if (updatedAmounts.some((a) => a != null)) payload.amounts = updatedAmounts;
    if (updatedDates.some((d) => d)) payload.dates = updatedDates;
    setRefCodeEdits({});
    verifyRefs(payload);
  };

  const toggleRef = useCallback((idx: number) => {
    setCheckedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setCheckedRefs(new Set(verifyResults.map((_, i) => i)));
      } else {
        setCheckedRefs(new Set<number>());
      }
    },
    [verifyResults]
  );

  // ── Step 3: Create invoice ──
  const { mutate: createInvoice, isLoading: creating } = useMutation(
    (payload: any) => CreateCommanWay({ url: "/admin/vendor-invoice/create", data: payload }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          setCreatedInvoice(res.data);
          setStep(3);
          UseToast(res?.message || "Invoice created successfully!", "success");
        } else {
          UseToast(res?.message || "Failed to create invoice", "error");
        }
      },
      onError: (err: any) => {
        UseToast(typeof err === "string" ? err : err?.message || "Failed to create invoice", "error");
      },
    }
  );

  const handleCreateAndPay = () => {
    if (!vendorId) {
      UseToast("Please select a vendor", "error");
      return;
    }
    if (!invoiceNo.trim()) {
      UseToast("Please enter an invoice number", "error");
      return;
    }

    if (checkedRefs.size === 0) {
      UseToast("Please select at least one ref code", "error");
      return;
    }

    // Build parallel refCodes and amounts arrays for checked items only
    const checkedRefCodes: string[] = [];
    const checkedAmounts: (number | null)[] = [];
    verifyResults.forEach((r, i) => {
      if (checkedRefs.has(i)) {
        checkedRefCodes.push(r.refCode);
        checkedAmounts.push(r.vendorAmount ?? null);
      }
    });

    Swal.fire({
      title: "Create Invoice & Mark Paid?",
      html: `<p>This will create the invoice and mark <b>${checkedRefs.size}</b> item(s) as vendor paid.</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Create & Pay",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    }).then((result) => {
      if (result.isConfirmed) {
        createInvoice({
          vendorId: vendorId.value,
          invoiceNo: invoiceNo.trim(),
          invoiceDate,
          periodFrom: periodFrom || undefined,
          periodTo: periodTo || undefined,
          totalAmount: totalAmount ? parseFloat(totalAmount) : undefined,
          verifiedAmount: verifiedAmount ? parseFloat(verifiedAmount) : undefined,
          remark: remark.trim() || undefined,
          refCodes: checkedRefCodes,
          amounts: checkedAmounts.length ? checkedAmounts : undefined,
        });
      }
    });
  };

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setExtractedRows([]);
    setFileType("");
    setStartRow(1);
    setEndRow(-1);
    setRefCodeCol(0);
    setAmountCol(-1);
    setDateCol(-1);
    setVerifyResults([]);
    setVerifySummary(null);
    setCheckedRefs(new Set());
    setVendorId(null);
    setInvoiceNo("");
    setInvoiceDate("");
    setPeriodFrom("");
    setPeriodTo("");
    setTotalAmount("");
    setVerifiedAmount("");
    setRemark("");
    setRemarkMap({});
    setCreatedInvoice(null);
  };

  // ──────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────
  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate("/billing/vendor-invoice")} className="text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-semibold">New Vendor Invoice</h1>
      </div>

      <StepIndicator current={step} />

      {/* ═══════════ STEP 1: Upload & Extract ═══════════ */}
      {step === 1 && (
        <div className="bg-white border rounded-lg p-5">
          <h2 className="text-lg font-medium mb-4">Upload Invoice File</h2>

          {/* File upload */}
          <div className="flex items-end gap-3 mb-5">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Select File (PDF, Excel, CSV, Image)</label>
              <input
                type="file"
                accept=".pdf,.xlsx,.xls,.csv,image/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            <Button color="primary" onPress={handleUpload} isLoading={extracting} startContent={<ArrowUpTrayIcon className="w-4 h-4" />}>
              Upload & Extract
            </Button>
          </div>

          {/* Extracted rows preview */}
          {extractedRows.length > 0 && (
            <>
              <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{fileType.toUpperCase()}</span>
                <span>{extractedRows.length} rows extracted</span>
              </div>

              {/* Row & Column pickers */}
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="w-48">
                  <label className="block text-sm font-medium mb-1">Items start at row</label>
                  <Select
                    options={rowOptions}
                    value={rowOptions.find((o) => o.value === startRow)}
                    onChange={(val: any) => setStartRow(val?.value ?? 1)}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="w-48">
                  <label className="block text-sm font-medium mb-1">Items end at row</label>
                  <Select
                    options={rowOptions}
                    value={rowOptions.find((o) => o.value === endRow) || rowOptions[rowOptions.length - 1]}
                    onChange={(val: any) => setEndRow(val?.value ?? extractedRows.length - 1)}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="w-60">
                  <label className="block text-sm font-medium mb-1">Ref code column <span className="text-red-500">*</span></label>
                  <Select
                    options={columnOptions}
                    value={columnOptions.find((o) => o.value === refCodeCol)}
                    onChange={(val: any) => setRefCodeCol(val?.value ?? 0)}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="w-60">
                  <label className="block text-sm font-medium mb-1">Amount column <span className="text-gray-400">(optional)</span></label>
                  <Select
                    options={[{ value: -1, label: "— None —" }, ...columnOptions]}
                    value={amountCol >= 0 ? columnOptions.find((o) => o.value === amountCol) : { value: -1, label: "— None —" }}
                    onChange={(val: any) => setAmountCol(val?.value ?? -1)}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="w-60">
                  <label className="block text-sm font-medium mb-1">Date column <span className="text-gray-400">(optional)</span></label>
                  <Select
                    options={[{ value: -1, label: "— None —" }, ...columnOptions]}
                    value={dateCol >= 0 ? columnOptions.find((o) => o.value === dateCol) : { value: -1, label: "— None —" }}
                    onChange={(val: any) => setDateCol(val?.value ?? -1)}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="flex items-end">
                  <div className="text-sm">
                    <span className="text-gray-500">Ref codes found: </span>
                    <span className="font-semibold text-blue-700">{extractedRefCodes.length}</span>
                  </div>
                </div>
              </div>

              {/* Table preview */}
              <div className="overflow-x-auto border rounded mb-4">
                <table className="min-w-full text-sm">
                  <tbody>
                    {extractedRows.map((row, ri) => {
                      const inRange = ri >= startRow && ri <= effectiveEndRow;
                      return (
                        <tr
                          key={ri}
                          className={`${!inRange ? "bg-gray-100 text-gray-400" : ""} ${ri === startRow ? "border-t-2 border-blue-500" : ""} ${ri === effectiveEndRow ? "border-b-2 border-blue-500" : ""}`}
                        >
                          <td className="px-2 py-1 text-xs text-gray-400 border-r w-10 text-center">{ri + 1}</td>
                          {row.map((cell, ci) => {
                            const isRef = ci === refCodeCol && inRange;
                            const isAmt = ci === amountCol && amountCol >= 0 && inRange;
                            const isDt = ci === dateCol && dateCol >= 0 && inRange;
                            return (
                              <td
                                key={ci}
                                className={`px-2 py-1 border-r whitespace-nowrap ${isRef ? "bg-blue-50 font-medium text-blue-800" : ""} ${isAmt ? "bg-green-50 text-green-800" : ""} ${isDt ? "bg-purple-50 text-purple-800" : ""}`}
                              >
                                {cell || ""}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button
                  color="primary"
                  onPress={handleGoToStep2}
                  isDisabled={extractedRefCodes.length === 0}
                  endContent={<ArrowRightIcon className="w-4 h-4" />}
                >
                  Extract & Verify ({extractedRefCodes.length} codes)
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════ STEP 2: Review & Verify ═══════════ */}
      {step === 2 && (
        <div className="bg-white border rounded-lg p-5">
          {verifying ? (
            <div className="flex flex-col items-center py-10">
              <Spinner />
              <p className="mt-3 text-gray-500 text-sm">Verifying ref codes...</p>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              {verifySummary && (
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    {verifySummary.total} total
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    {verifySummary.valid} valid
                  </span>
                  {verifySummary.duplicate > 0 && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                      {verifySummary.duplicate} duplicate
                    </span>
                  )}
                  {verifySummary.alreadyPaid > 0 && (
                    <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      {verifySummary.alreadyPaid} already paid
                    </span>
                  )}
                  {verifySummary.cancelled > 0 && (
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      {verifySummary.cancelled} cancelled
                    </span>
                  )}
                  {verifySummary.notFound > 0 && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                      {verifySummary.notFound} not found
                    </span>
                  )}
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {checkedRefs.size} selected
                  </span>
                  {hasRefCodeEdits && (
                    <button
                      onClick={handleReverify}
                      disabled={verifying}
                      className="px-3 py-1 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 rounded-full text-sm font-semibold transition-colors"
                    >
                      ↻ Re-verify
                    </button>
                  )}
                  {verifySummary.totalVendorAmount > 0 && (
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                      Vendor Total: ₹{verifySummary.totalVendorAmount.toLocaleString("en-IN")}
                    </span>
                  )}
                  {verifySummary.totalValidAmount > 0 && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      Valid Amt: ₹{verifySummary.totalValidAmount.toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              )}

              {/* Results table */}
              <div className="overflow-x-auto border rounded mb-6">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={checkedRefs.size === verifyResults.length && verifyResults.length > 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                          className="rounded"
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref Code</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Our Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Diff</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remark</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {verifyResults.map((r, i) => (
                      <tr
                        key={i}
                        className={`hover:bg-gray-50 ${!checkedRefs.has(i) ? "opacity-50" : ""} ${r.dateWarning ? "bg-orange-50" : ""}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={checkedRefs.has(i)}
                            onChange={() => toggleRef(i)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-3 py-2 font-mono font-medium">
                          <input
                            type="text"
                            value={refCodeEdits[i] !== undefined ? refCodeEdits[i] : r.refCode}
                            onChange={(e) =>
                              setRefCodeEdits((prev) => ({ ...prev, [i]: e.target.value }))
                            }
                            className={`w-full border rounded px-2 py-0.5 text-sm font-mono ${
                              refCodeEdits[i] !== undefined && refCodeEdits[i] !== r.refCode
                                ? "border-yellow-400 bg-yellow-50"
                                : "border-transparent bg-transparent"
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2 capitalize">{r.type}</td>
                        <td className="px-3 py-2">{r.status || "-"}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${flagColors[r.flag] || "bg-gray-100"}`}>
                            {r.flag.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {r.vendorAmount != null ? `₹${r.vendorAmount.toLocaleString("en-IN")}` : "-"}
                        </td>
                        <td className="px-3 py-2">{r.vendorDate || "-"}</td>
                        <td className="px-3 py-2">{r.ourDate || "-"}</td>
                        <td className={`px-3 py-2 ${r.dateWarning ? "text-orange-700 font-semibold" : ""}`}>
                          {r.dateDiffDays != null ? `${r.dateDiffDays > 0 ? "+" : ""}${r.dateDiffDays}d` : "-"}
                        </td>
                        <td className="px-3 py-2 text-xs max-w-[200px]">
                          <input
                            type="text"
                            value={remarkMap[r.refCode] || ""}
                            onChange={(e) =>
                              setRemarkMap((prev) => ({ ...prev, [r.refCode]: e.target.value }))
                            }
                            className="w-full border rounded px-2 py-1 text-xs"
                            placeholder="Enter remark"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Invoice form */}
              <h3 className="text-md font-medium mb-3 border-t pt-4">Invoice Details</h3>
              <div className="grid grid-cols-3 md:grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Vendor <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={vendorOptions}
                    value={vendorId}
                    onChange={(val: any) => setVendorId(val)}
                    placeholder="Select Vendor"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Invoice No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="e.g. VND-2026-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Invoice Date</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Total Amount (Vendor's claimed)</label>
                  <input
                    type="number"
                    value={totalAmount}
                    onChange={(e) => setTotalAmount(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Verified Amount (Approved)</label>
                  <input
                    type="number"
                    value={verifiedAmount}
                    onChange={(e) => setVerifiedAmount(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Remark</label>
                  <input
                    type="text"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="General notes..."
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between border-t pt-4">
                <Button variant="bordered" onPress={() => setStep(1)} startContent={<ArrowLeftIcon className="w-4 h-4" />}>
                  Back
                </Button>
                <Button color="primary" onPress={handleCreateAndPay} isLoading={creating} isDisabled={checkedRefs.size === 0}>
                  Create & Pay ({checkedRefs.size} items)
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ═══════════ STEP 3: Confirmation ═══════════ */}
      {step === 3 && (
        <div className="bg-white border rounded-lg p-8 text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Invoice Created Successfully!</h2>
          {createdInvoice && (
            <div className="text-gray-600 mb-6 space-y-1">
              <p>
                Invoice No: <span className="font-medium">{createdInvoice.invoiceNo}</span>
              </p>
              <p>
                Status: <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-sm">{createdInvoice.status}</span>
              </p>
              <p>
                Total: <span className="font-medium">₹{createdInvoice.totalAmount?.toLocaleString("en-IN") || "0"}</span> | Verified:{" "}
                <span className="font-medium">₹{createdInvoice.verifiedAmount?.toLocaleString("en-IN") || "0"}</span>
              </p>
            </div>
          )}
          <div className="flex justify-center gap-3">
            {createdInvoice?.id && (
              <Button color="primary" variant="bordered" onPress={() => navigate(`/billing/vendor-invoice/${createdInvoice.id}`)}>
                View Invoice
              </Button>
            )}
            <Button color="primary" onPress={resetWizard}>
              Create Another
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorInvoiceCreate;
